import {
  FC, memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import { Checkbox } from 'antd';
import { Title } from 'shared/ui';
import { useAppDispatch, useAppSelector } from 'app/providers/Store';
import { getEngineMeasurementsByEngineId } from 'entities/engineMeasurement/model/actions/actions';
import { engineMeasurementActions } from 'entities/engineMeasurement/model/slice/engineMeasurement.slice';
import { engineMeasurementSelectors } from 'entities/engineMeasurement/model/selectors/engineMeasurementSelectors';
import {
  Line, Tooltip, XAxis, YAxis,
  CartesianGrid, LineChart,
} from 'recharts';
import dayjs from 'dayjs';
import { ENGINE_MEASUREMENT_CHART_SERIES } from 'pages/Engines/constants/engineMeasurementConstants';
import styles from './EngineMeasurementSection.module.scss';
import { MeasurementAxisTick } from './MeasurementAxisTick';
import { EngineMeasurementForm } from '../EngineMeasurementForm';

interface EngineMeasurementSectionProps {
  engineId: number | null;
}

const getInitialVisibleSeries = (): Record<string, boolean> => (
  ENGINE_MEASUREMENT_CHART_SERIES.reduce<Record<string, boolean>>((acc, { dataKey }) => {
    acc[dataKey] = true;
    return acc;
  }, {})
);

export const EngineMeasurementSection: FC<EngineMeasurementSectionProps> = memo(({
  engineId,
}) => {
  const [isMeasurementFormOpen, setIsMeasurementFormOpen] = useState(false);
  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>(getInitialVisibleSeries);
  const engineMeasurements = useAppSelector(engineMeasurementSelectors.getAllEngineMeasurements);
  const sortedEngineMeasurements = useMemo(
    () => [...engineMeasurements]?.sort((a, b) => a.engineHours - b.engineHours) || [],
    [engineMeasurements],
  );
  const dispatch = useAppDispatch();

  const handleLegendToggle = useCallback((dataKey: string) => {
    setVisibleSeries((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
  }, []);

  useEffect(() => {
    if (engineId) {
      dispatch(getEngineMeasurementsByEngineId(engineId));
    }
    return () => {
      dispatch(engineMeasurementActions.dropMeasurementsData());
    };
  }, [engineId]);

  const equivalentByEngineHours = useMemo(() => {
    const map = new Map<number, number>();
    sortedEngineMeasurements.forEach(({ engineHours, equivalentHours }) => {
      if (engineHours !== undefined && equivalentHours !== undefined) {
        map.set(engineHours, equivalentHours);
      }
    });
    return map;
  }, [sortedEngineMeasurements]);

  const lastEngineMeasurement = useMemo(() => {
    if (sortedEngineMeasurements.length === 0) return null;
    return sortedEngineMeasurements[sortedEngineMeasurements.length - 1];
  }, [sortedEngineMeasurements]);

  const handleAddMeasurement = () => {
    setIsMeasurementFormOpen(true);
  };

  const handleMeasurementFormClose = () => {
    setIsMeasurementFormOpen(false);
  };

  const handleMeasurementSuccess = () => {
    // Перезагружаем измерения: от них зависят и график, и шкала инспекций.
    if (engineId) {
      dispatch(getEngineMeasurementsByEngineId(engineId));
    }
  };

  return (
    <div className={styles.measurementSection}>
      <div className={styles.measurementHeader}>
        <Title level={5}>Измерения двигателя</Title>
        <button
          type="button"
          className={styles.addMeasurementButton}
          onClick={handleAddMeasurement}
        >
          Добавить измерения
        </button>
      </div>

      {sortedEngineMeasurements.length > 0 && (
        <div className={styles.measurementContent}>
          {/* Статистика и обслуживание */}
          <div className={styles.statsSection}>
            <div className={styles.statsColumn}>
              <div className={styles.statsCard}>
                <Title level={4}>
                  Последние показатели измерений
                  (от
                  {' '}
                  {lastEngineMeasurement?.createdAt ? dayjs(lastEngineMeasurement.createdAt).format('DD.MM.YYYY') : ''}
                  )
                </Title>
                <div className={styles.statsInfo}>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Количество пусков</span>
                    <span className={styles.value}>{lastEngineMeasurement?.countOfStarts}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Температура статора, фаза U</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.temperatureStatorU}
                      {' '}
                      °C
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Температура статора, фаза V</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.temperatureStatorV}
                      {' '}
                      °C
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Температура статора, фаза W</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.temperatureStatorW}
                      {' '}
                      °C
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Вибрация DE</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.vibrationDe}
                      {' '}
                      мм/с
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Вибрация NDE</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.vibrationNde}
                      {' '}
                      мм/с
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Температура подшипников DE</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.temperatureBrgDe}
                      {' '}
                      °C
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Температура подшипников NDE</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.temperatureBrgNde}
                      {' '}
                      °C
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Температура воздуха H</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.temperatureAirH}
                      {' '}
                      °C
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Температура воздуха C</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.temperatureAirC}
                      {' '}
                      °C
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Наработка машины</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.engineHours}
                      {' '}
                      ч
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Эквивалентные часы</span>
                    <span className={styles.value}>
                      {lastEngineMeasurement?.equivalentHours}
                      {' '}
                      ч
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.statsColumn}>
              <div className={styles.statsCard}>
                <Title level={4}>Статистика по моточасам</Title>
                <div className={styles.chartContainer}>
                  <LineChart
                    style={{
                      width: '100%', maxWidth: '700px', maxHeight: '70vh', aspectRatio: 1.618,
                    }}
                    responsive
                    data={sortedEngineMeasurements}
                    margin={{
                      top: 15,
                      right: 0,
                      left: 0,
                      bottom: 20,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="engineHours"
                      height={48}
                      tick={<MeasurementAxisTick equivalentByEngineHours={equivalentByEngineHours} />}
                    />
                    <YAxis
                      yAxisId="left"
                      width="auto"
                      label={{
                        value: 'Температура / часы', angle: -90, position: 'insideLeft',
                      }}
                      unit=""
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      width="auto"
                      label={{
                        value: 'Вибрация', angle: 90, position: 'insideRight',
                      }}
                      unit="мм/с"
                    />
                    <Tooltip labelFormatter={(value) => `${value} ч`} />
                    {ENGINE_MEASUREMENT_CHART_SERIES.map((series) => {
                      if (!visibleSeries[series.dataKey]) return null;
                      return (
                        <Line
                          key={series.dataKey}
                          yAxisId={series.yAxisId}
                          type="monotone"
                          dataKey={series.dataKey}
                          name={series.name}
                          stroke={series.stroke}
                          activeDot={{ r: 6 }}
                          unit={series.unit}
                          connectNulls
                        />
                      );
                    })}
                  </LineChart>
                  <div className={styles.chartLegend}>
                    {ENGINE_MEASUREMENT_CHART_SERIES.map(({ dataKey, name, stroke }) => (
                      <div
                        key={dataKey}
                        role="button"
                        tabIndex={0}
                        className={styles.legendItem}
                        onClick={() => handleLegendToggle(dataKey)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleLegendToggle(dataKey);
                          }
                        }}
                      >
                        <Checkbox
                          checked={visibleSeries[dataKey] ?? true}
                          onChange={() => handleLegendToggle(dataKey)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span
                          className={styles.legendColor}
                          style={{
                            backgroundColor: visibleSeries[dataKey] ? stroke : '#d9d9d9',
                          }}
                        />
                        <span className={styles.legendLabel}>{name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isMeasurementFormOpen && engineId && (
        <EngineMeasurementForm
          engineId={engineId}
          onClose={handleMeasurementFormClose}
          onSuccess={handleMeasurementSuccess}
        />
      )}
    </div>
  );
});

EngineMeasurementSection.displayName = 'EngineMeasurementSection';
